/* eslint-disable @typescript-eslint/no-unused-vars */
import { useContext, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { colors } from '../../../style';
import infoIcon from '../../../images/info-circle.svg';
import infoCircleOutlineBlack from '../../../images/info-circle-outline-black.svg';
import arrow from '../../../images/down_arrow_blue.svg';
import {
  get,
  prefixObjectValues,
  enums,
  getLocalizedOrDefault,
} from '../../../utils';
import { Reveal } from '@progress/kendo-react-animation';
import * as dm from '../../../data-models';
import useRefreshHandler from '../../../hooks/use-refresh-handler';
import FadeLoader from 'react-spinners/FadeLoader';
import { Column, dateFormat } from './card-items/shared-styles';
import { StatusFlagModified } from './components/tw-components';
import {
  Text,
  baseShadowSmall,
  baseShadowMedium,
} from '../../base/tw-components';
import { isDueTask, taskAppliesToNTN } from '../../tasks/utils';
import {
  sortAssignedTasks,
  checkForRequiredTasks,
  QuickActionsDropDown,
} from './utils';
import { OverviewTab } from './tabs/overview';
import { DetailsTab } from './tabs/details';
import { PaymentsTab } from './tabs/payments';
import {
  StatusTrackerHorizontal,
  StatusTrackerVertical,
} from './status-tracker';
import dayjs from 'dayjs';
import { KenticoDataContext } from '../../../contexts/kentico-data-context';
import { PreferencesContext } from '../../../contexts/preferences-context';
import { CaseSubstatusCodenames } from '../../../utils/enums';
import { RedirectState } from '../../summary';
import { useHistory } from 'react-router-dom';
export interface IGeneratedCase {
  NTNId: string;
  accommodations: Array<dm.Accommodation>;
  claims: Array<dm.Claim>;
  leaves: Array<dm.Leave>;
  isIntegrated: boolean;
}

interface IClaimCard {
  keyId?: number;
  claim: IGeneratedCase;
  ERTasks: Array<dm.Task>;
  EETasks: Array<dm.Task>;
  failedWithData?: boolean;
  allReceivedTasks?: Array<dm.Task>;
  absenceNotifications?: dm.IAbsenceNotifications;
  personData?: dm.PersonData;
  allTasks?: any;
  showPayments?: boolean;
  redirectLocation?: RedirectState;
  ntnToOpen?: string;
  ptoChoice?: string;
}

type CardBodyType = {
  expand: boolean;
};

const Card = styled.div`
  background: #fff;

  max-width: 992px;
  overflow: hidden;
  width: 100%;

  @media (min-width: 640px) {
    border-radius: 8px;
    box-shadow: 0 3px 6px rgba(46, 46, 46, 0.16);
  }

  @media (min-width: 768px) {
    margin-right: 1em;
  }

  .k-animation-container {
    z-index: 1000 !important;
  }
`;

export const Arrow = styled.img.attrs({
  alt: 'Expand',
  src: arrow,
})`
  cursor: pointer;
  margin-right: -10px;
  transform: ${(props: { rotate: string }) =>
    props.rotate === 'true' ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: all 0.5s;
  user-select: none;
  width: 2.5em;
`;

const CardHeader = styled.div`
  align-items: stretch;
  background: #fff;
  border-left: solid 16px #037cb7;
  border-radius: 8px;
  box-shadow: 0 3px 6px rgba(46, 46, 46, 0.16);
  display: flex;
  position: relative;
  width: 100%;
  z-index: 2;
`;

const CardBody = styled.div<CardBodyType>`
  height: ${(p) => (p.expand ? '100%' : '0')};
  overflow: hidden;
  visibility: ${(p) => (p.expand ? 'visible' : 'hidden')};
  width: 100%;

  &:first-child {
    height: 100%;
    visibility: visible;
  }
`;

const LoadingCover = styled.div`
  height: 110px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;

  & > span {
    width: 48px;
    height: 48px;
  }
`;

const CardItemsContainer = styled.div`
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 32px;
  padding-right: 32px;
  width: 100%;
`;

const domIdsStatic = {
  rootNode: 'claims-card',
  cardTitle: 'claim-card-title',
  dropDownArrow: 'dropdown-arrow',
};

export const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

const serviceLevelIsEnhanced = (
  ERPrefs: dm.EmployerPreferences | undefined,
): boolean => {
  if (!ERPrefs?.preferences) {
    return false;
  }

  return (
    ERPrefs.preferences.hasOwnProperty('ADACLASSTYPE') &&
    ERPrefs?.preferences['ADACLASSTYPE'] === 'ADA - Enhanced'
  );
};

const hasOpenOrPendingGdcWithRTW = (claim: IGeneratedCase) => {
  return claim.claims.some(
    (gdc) =>
      gdc.expectedReturnToWorkDate &&
      (gdc.status === 'Open' || gdc.status === 'Pending'),
  );
};

const absAndGdcAreApprovedAndClosed = (claim: IGeneratedCase) => {
  const absApprovedAndClosed = claim.leaves.some(
    (abs) =>
      (abs.status === 'Completion' || abs.status === 'Closed') &&
      abs.requestStatus === 'Approved',
  );
  const gdcClosed = claim.claims.some((gdc) => gdc.status === 'Closed');

  return absApprovedAndClosed && gdcClosed;
};

const wasIntermittentNowIsContinuousLeave = (
  claim: IGeneratedCase,
  currentDate: string,
) => {
  const wasIntermittent = claim.leaves.some((abs) => {
    if (!abs.absencePeriods) {
      return false;
    }
    return abs.absencePeriods.some(
      (period) =>
        dayjs(currentDate).isAfter(period.endDate) &&
        period.absenceType === 'Intermittent',
    );
  });

  const nowContinuous = claim.leaves.some((abs) => {
    if (!abs.absencePeriods) {
      return false;
    }
    return abs.absencePeriods.some(
      (period) =>
        isActivePeriod(currentDate, period.startDate, period.endDate) &&
        period.absenceType === 'Continuous',
    );
  });

  return wasIntermittent && nowContinuous;
};

const isActivePeriod = (
  currentDate: string,
  startDate: string | undefined,
  endDate: string | undefined,
): boolean => {
  if (!startDate || !endDate) {
    return false;
  }

  if (dayjs(currentDate).isBefore(dayjs(startDate))) {
    return false;
  }

  if (dayjs(currentDate).isAfter(dayjs(endDate))) {
    return false;
  }

  return true;
};

const containsAbsenceTakeover = (claim: IGeneratedCase) => {
  return (
    claim.leaves.filter((leave) => {
      if (!leave.absencePeriods || !leave.caseId?.startsWith('T ABS-')) {
        return false;
      }

      return true;
    }).length > 0
  );
};

const containsMonitoringAcm = (claim: IGeneratedCase) => {
  return claim.accommodations.some((acm) => acm.status === 'Monitoring');
};

const containsExploreAddtlOptionsAcm = (claim: IGeneratedCase) => {
  return claim.accommodations.some(
    (acm) => acm.status === 'Explore Additional Options',
  );
};

const getAbsencePeriodsFromLeaves = (leaves: dm.Leave[]) => {
  return leaves.length > 0 ? leaves[0].absencePeriods : null;
};

const getActiveLeaveType = (claim: IGeneratedCase, currentDate: string) => {
  const openLeaves = claim.leaves.filter(
    (leave) =>
      leave.status !== 'Closed' &&
      leave.status !== 'Completion' &&
      leave.reason !== 'Leave as an Accommodation',
  );

  const leavesAsAccommodation = claim.leaves.filter(
    (leave) =>
      leave.status !== 'Closed' &&
      leave.status !== 'Completion' &&
      leave.reason === 'Leave as an Accommodation',
  );

  const openLeaveAbsencePeriods = getAbsencePeriodsFromLeaves(openLeaves);

  const openLeaveAsAccomAbsencePeriods = getAbsencePeriodsFromLeaves(
    leavesAsAccommodation,
  );

  const intermittentLeaveAsAccomAbsPeriods = openLeaveAsAccomAbsencePeriods
    ? openLeaveAsAccomAbsencePeriods.filter(
        (period) => period.absenceType === 'Intermittent',
      ).length > 0
    : null;

  if (!openLeaveAbsencePeriods) {
    if (intermittentLeaveAsAccomAbsPeriods) {
      return 'Intermittent Leave as Accommodation Only';
    } else {
      return null;
    }
  }

  const hasIntermittentPeriods =
    openLeaveAbsencePeriods.filter(
      (period) => period.absenceType === 'Intermittent',
    ).length > 0;

  const hasContinuousPeriods =
    openLeaveAbsencePeriods.filter(
      (period) => period.absenceType === 'Continuous',
    ).length > 0;

  if (hasIntermittentPeriods && hasContinuousPeriods) {
    const activePeriod = openLeaveAbsencePeriods.find((period) => {
      return isActivePeriod(currentDate, period.startDate, period.endDate);
    });

    if (activePeriod?.absenceType) {
      return activePeriod.absenceType;
    }
  }

  if (hasIntermittentPeriods && !hasContinuousPeriods) {
    return 'Intermittent';
  }

  if (!hasIntermittentPeriods && hasContinuousPeriods) {
    return 'Continuous';
  }

  return null;
};

const getCardTitle = (claim: any) => {
  if (claim?.leaves?.length > 0) {
    return claim.leaves[0].reason ===
      enums.PregnancyTextChanges.pregnancyBondingLiteral
      ? enums.PregnancyTextChanges.pregnancyBondingLiteralNew
      : claim.leaves[0].reason;
  } else if (claim?.claims?.length > 0) {
    return claim.claims[0].claimType;
  } else if (claim?.accommodations?.length > 0) {
    return 'Accommodation Request';
  }
  return 'N/A';
};

type ContextualMessage = {
  label: string;
  heading: string;
  bullets: string[];
};

const getContextualMessage = (
  stage: string,
  hasEETasks: boolean,
  isIntermittent: boolean,
): ContextualMessage | null => {
  switch (stage) {
    case 'Received':
      return {
        label: 'Received',
        heading:
          'Thank you for your submitting your need for leave on a continuous schedule.',
        bullets: [
          'We have successfully received your information and are currently processing it.',
          'Please monitor your status on the portal over the next 48 hours for updates on your case.',
        ],
      };
    case 'Review':
      return {
        label: 'Review',
        heading:
          'Your case has been assigned to a member of our team and is in review.',
        bullets: [
          'Please monitor your Tasks and Letters from Unum for updates and information that may be needed in order to complete our review.',
          'A member of our team will notify you once a decision has been made.',
        ],
      };
    case 'Active':
      return {
        label: 'Active',
        heading: 'A decision has been made on your case.',
        bullets: [
          ...(hasEETasks
            ? ['You also have Tasks to complete by the due date provided.']
            : []),
          'Please review the Details section below.',
          ...(isIntermittent
            ? [
                "Continue to report your intermittent absences to Unum within your employer's notice requirement.",
              ]
            : []),
          'Review your Letters from Unum for additional details and see the enclosed Important Information in your letter.',
        ],
      };
    case 'Closed':
      return {
        label: 'Closed',
        heading: 'Your case has been closed.',
        bullets: [
          'Please refer to your Letters from Unum and Details section below for more information on the status of your case.',
        ],
      };
    default:
      return null;
  }
};

const hasDecidedLeaves = (claim: IGeneratedCase): boolean => {
  const leaves = claim?.leaves;
  const selectedLeavePlans = leaves?.map((l) => l.selectedLeavePlans);
  const acm = claim?.accommodations;

  if (selectedLeavePlans.length > 0) {
    for (const element of selectedLeavePlans) {
      if (
        element?.some((plan) => plan.adjudicationStatus === 'Approved') ||
        element?.some((plan) => plan.adjudicationStatus === 'Accepted') ||
        element?.some((plan) => plan.adjudicationStatus === 'Mixed Decision') ||
        element?.some((plan) => plan.adjudicationStatus === 'Rejected') ||
        element?.some((plan) => plan.adjudicationStatus === 'Denied')
      ) {
        return true;
      }
    }
  }

  if (acm?.length > 0) {
    for (const element of acm) {
      if (
        element.status === 'Accepted Accommodation' ||
        element.status === 'Accommodated' ||
        element.status === 'Explore Additional Options' ||
        element.status === 'Monitoring'
      ) {
        return true;
      }
    }
  }
  return false;
};

const getDecisionLetters = (claim: IGeneratedCase, letters: dm.Document[]) => {
  return letters?.filter(
    (l) =>
      (l.name === 'ADA Decision Notice' ||
        l.name === 'Designation Notice Letter' ||
        l.name === 'EP Not Satisfied Letter' ||
        l.name === 'Integrated Approval Letter' ||
        l.name === 'Integrated Approval letter' ||
        l.name === 'Leave Cancellation' ||
        l.name === 'Leave Denial Letter') &&
      (l.claimId?.includes(claim.NTNId) || l.leaveId?.includes(claim.NTNId)),
  );
};

const getReviewingIntermittentScenario = (
  claim: IGeneratedCase,
  hasReqTasks: boolean,
  isFutureStartDate: boolean,
  serviceLevelEnhanced: boolean,
  leaveAsAccomOnly: boolean,
) => {
  if (
    serviceLevelEnhanced &&
    claim.accommodations.some(
      (acm) =>
        (acm.status === 'Request Recognized' ||
          acm.status === 'Gather Additional Information') &&
        acm.accommodationDetails?.some(
          (detail) => detail.category === 'Leave as an Accommodation',
        ),
    )
  ) {
    return CaseSubstatusCodenames.reviewing_2_10;
  }

  if (
    serviceLevelEnhanced &&
    claim.accommodations.some(
      (acm) =>
        acm.status === 'Evaluate Accommodation Options' &&
        acm.accommodationDetails?.some(
          (detail) => detail.category === 'Leave as an Accommodation',
        ),
    )
  ) {
    return CaseSubstatusCodenames.reviewing_2_11;
  }

  if (!leaveAsAccomOnly) {
    if (!hasReqTasks) {
      return CaseSubstatusCodenames.reviewing_2_4;
    }

    if (isFutureStartDate) {
      return CaseSubstatusCodenames.reviewing_2_6;
    }

    if (!isFutureStartDate) {
      return CaseSubstatusCodenames.reviewing_2_5;
    }
  }

  return null;
};

const getManagementIntermittentScenario = (
  claim: IGeneratedCase,
  currentDate: string,
  hasReqTasks: boolean,
  hasDesignationNotice: boolean,
  isTakeover: boolean,
) => {
  if (
    hasReqTasks &&
    hasDesignationNotice &&
    wasIntermittentNowIsContinuousLeave(claim, currentDate) &&
    hasOpenOrPendingGdcWithRTW(claim)
  ) {
    return CaseSubstatusCodenames.management_3_10;
  }

  if (hasReqTasks && isTakeover) {
    return CaseSubstatusCodenames.management_3_9;
  }

  if (!hasReqTasks && isTakeover) {
    return CaseSubstatusCodenames.management_3_8;
  }

  if (hasReqTasks && !isTakeover) {
    return CaseSubstatusCodenames.management_3_7;
  }

  return CaseSubstatusCodenames.management_3_6;
};

export const trackerStage = (
  parentCase: { status: string; createdDate: string } | undefined,
  claim: IGeneratedCase,
  currentDate?: string,
  letters: dm.Document[] = [],
) => {
  const decidedLeaves = hasDecidedLeaves(claim);
  const decisionLetters = getDecisionLetters(claim, letters);
  if (parentCase?.status === 'Closed') {
    return 'Closed';
  } else if (
    decidedLeaves &&
    (decisionLetters.length > 0 ||
      containsAbsenceTakeover(claim) ||
      containsMonitoringAcm(claim) ||
      containsExploreAddtlOptionsAcm(claim))
  ) {
    return 'Active';
  } else if (dayjs(currentDate).isAfter(dayjs(parentCase?.createdDate))) {
    return 'Review';
  } else {
    return 'Received';
  }
};

export const getReceivedScenario = (
  claim: IGeneratedCase,
  currentDate: string,
): CaseSubstatusCodenames => {
  const isIntermittent =
    getActiveLeaveType(claim, currentDate) === 'Intermittent';

  if (isIntermittent) {
    return CaseSubstatusCodenames.received_1_2;
  }

  if (claim.accommodations.length > 0) {
    return CaseSubstatusCodenames.received_1_4;
  }

  return CaseSubstatusCodenames.received_1_1;
};

export const getReviewingScenario = (
  claim: IGeneratedCase,
  caseTasks: dm.Task[],
  currentDate: string,
  serviceLevelEnhanced: boolean,
): CaseSubstatusCodenames => {
  const startDates = claim.leaves
    .filter((l) => l.status !== 'Closed' && l.status !== 'Completion')
    .map((l) => l.startDate);
  startDates.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  const reqTasks = caseTasks?.filter(
    (t) =>
      (t.assigneeId === 2 || t.dualTask === true) &&
      t.taskCategory === 'Required' &&
      t.taskStatus !== 'Archived' &&
      t.taskStatusId !== 5 &&
      t.taskStatusId !== 7,
  );
  const periodType = getActiveLeaveType(claim, currentDate);
  const hasReqTasks = reqTasks?.length > 0;
  const isFutureStartDate = dayjs(currentDate).isBefore(dayjs(startDates[0]));
  if (
    periodType === 'Intermittent' ||
    periodType === 'Intermittent Leave as Accommodation Only'
  ) {
    const intermittentScenario = getReviewingIntermittentScenario(
      claim,
      hasReqTasks,
      isFutureStartDate,
      serviceLevelEnhanced,
      periodType === 'Intermittent Leave as Accommodation Only',
    );
    if (intermittentScenario) {
      return intermittentScenario;
    }
  }

  if (periodType === 'Continuous' && !hasReqTasks) {
    return CaseSubstatusCodenames.reviewing_2_1;
  }

  if (periodType === 'Continuous' && isFutureStartDate) {
    return CaseSubstatusCodenames.reviewing_2_3;
  }

  if (periodType === 'Continuous') {
    return CaseSubstatusCodenames.reviewing_2_2;
  }

  if (
    claim.accommodations.some(
      (acm) =>
        (acm.status === 'Request Recognized' ||
          acm.status === 'Gather Additional Information') &&
        (acm.accommodationDetails?.some(
          (detail) => detail.category !== 'Leave as an Accommodation',
        ) ||
          (acm.accommodationDetails?.some(
            (detail) => detail.category === 'Leave as an Accommodation',
          ) &&
            !serviceLevelEnhanced)) &&
        !hasReqTasks,
    )
  ) {
    return CaseSubstatusCodenames.reviewing_2_7;
  }

  if (
    claim.accommodations.some(
      (acm) =>
        acm.status === 'Gather Additional Information' &&
        (acm.accommodationDetails?.some(
          (detail) => detail.category !== 'Leave as an Accommodation',
        ) ||
          (acm.accommodationDetails?.some(
            (detail) => detail.category === 'Leave as an Accommodation',
          ) &&
            !serviceLevelEnhanced)),
    ) &&
    hasReqTasks
  ) {
    return CaseSubstatusCodenames.reviewing_2_8;
  }

  if (
    claim.accommodations.some(
      (acm) =>
        acm.status === 'Evaluate Accommodation Options' &&
        (acm.accommodationDetails?.some(
          (detail) => detail.category !== 'Leave as an Accommodation',
        ) ||
          (acm.accommodationDetails?.some(
            (detail) => detail.category === 'Leave as an Accommodation',
          ) &&
            !serviceLevelEnhanced)),
    ) &&
    !hasReqTasks
  ) {
    return CaseSubstatusCodenames.reviewing_2_9;
  }

  return CaseSubstatusCodenames.reviewing_2_1;
};

export const getManagementScenario = (
  claim: IGeneratedCase,
  letters: dm.Document[],
  caseTasks: dm.Task[],
  currentDate: string,
): CaseSubstatusCodenames => {
  const startDates = claim.leaves
    .filter((l) => l.status !== 'Closed' && l.status !== 'Completion')
    .map((l) => l.startDate);
  startDates.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  const reqTasks = caseTasks?.filter(
    (t) =>
      (t.assigneeId === 2 || t.dualTask === true) &&
      t.taskCategory === 'Required' &&
      t.taskStatus !== 'Archived' &&
      t.taskStatusId !== 5 &&
      t.taskStatusId !== 7,
  );

  const periodType = getActiveLeaveType(claim, currentDate);
  const isTakeover = containsAbsenceTakeover(claim);
  const isFutureStartDate = dayjs(currentDate).isBefore(dayjs(startDates[0]));
  const hasReqTasks = reqTasks?.length > 0;
  const hasDesignationNotice = getDecisionLetters(claim, letters).some(
    (l) => l.name === 'Designation Notice',
  );
  const absAndGdcApprovedAndClosed = absAndGdcAreApprovedAndClosed(claim);

  if (periodType === 'Intermittent') {
    return getManagementIntermittentScenario(
      claim,
      currentDate,
      hasReqTasks,
      hasDesignationNotice,
      isTakeover,
    );
  }

  if (periodType === 'Continuous' && hasReqTasks && !isFutureStartDate) {
    return CaseSubstatusCodenames.management_3_3;
  }

  if (periodType === 'Continuous' && hasReqTasks && isFutureStartDate) {
    return CaseSubstatusCodenames.management_3_2;
  }

  if (periodType === 'Continuous' && !hasReqTasks) {
    return CaseSubstatusCodenames.management_3_1;
  }

  if (
    absAndGdcApprovedAndClosed &&
    claim.accommodations.some(
      (acm) =>
        acm.status === 'Request Recognized' ||
        acm.status === 'Gather Additional Information',
    )
  ) {
    return CaseSubstatusCodenames.management_3_14;
  }

  if (containsMonitoringAcm(claim)) {
    return CaseSubstatusCodenames.management_3_12;
  }

  if (containsExploreAddtlOptionsAcm(claim)) {
    return CaseSubstatusCodenames.management_3_13;
  }

  return CaseSubstatusCodenames.management_3_1;
};

const alwaysVisibleTabs = {
  overview: 'Overview',
  details: 'Details',
};

const addConditionallyVisibleTabs = (
  setVisible: Function,
  paymentsVisible: boolean | undefined = false,
) => {
  const tabList = {
    ...alwaysVisibleTabs,
    ...(paymentsVisible ? { payments: 'Payments' } : {}),
  };

  setVisible(tabList);
};

const getLettersForThisNTN = (
  claim: IGeneratedCase,
  letters: dm.Document[],
): boolean => {
  return (
    letters?.some(
      (l) =>
        l.claimId?.includes(claim.NTNId) || l.leaveId?.includes(claim.NTNId),
    ) ?? false
  );
};

export type ExpandedType = (
  p: {
    NTNId: string;
    ariaExpand: boolean;
    areTasksDue?: boolean;
    failedWithData?: boolean;
    subStatusText: any;
    currentDate: string;
    letters: dm.Document[];
    status?: string;
    hasLetters?: boolean;
    tab?: string;
    setTab: React.Dispatch<React.SetStateAction<string>>;
  } & IClaimCard,
) => React.ReactElement;

const ExpandedSection: ExpandedType = (p) => {
  const {
    claim,
    ariaExpand,
    NTNId,
    EETasks,
    failedWithData,
    absenceNotifications,
    currentDate,
    letters,
    showPayments,
    tab,
    setTab,
    ptoChoice,
  } = p;
  const allLeaveIds = claim?.leaves?.map((l) => l.leaveId ?? '');
  const allClaimIds = claim?.claims?.map((l) => l.claimId ?? '');
  const allAccommodationsIds = claim?.accommodations?.map(
    (l) => l.accommodationId ?? '',
  );

  const hasLetters = getLettersForThisNTN(claim, letters);

  const allIds = [...allLeaveIds, ...allClaimIds, ...allAccommodationsIds];
  const { status } = useRefreshHandler(
    [enums.RefreshDetailTypes.caseDetails],
    allIds,
  );
  const parentCase = absenceNotifications?.absenceNotifications?.find(
    (n) => n.notificationCaseId === NTNId,
  );

  const currentStatus = trackerStage(parentCase, claim, currentDate, letters);
  const [visibleTabs, setVisibleTabs] = useState({ ...alwaysVisibleTabs });

  enum tabs {
    overview = 'Overview',
    details = 'Details',
    payments = 'Payments',
  }

  useEffect(
    () => addConditionallyVisibleTabs(setVisibleTabs, showPayments),
    [showPayments],
  );

  const renderTabContent = () => {
    let tabContent;

    switch (tab) {
      case tabs.details:
        tabContent = (
          <DetailsTab
            {...p}
            status={currentStatus}
            hasLetters={hasLetters}
            absenceNotifications={absenceNotifications}
            ptoChoice={ptoChoice}
          />
        );
        break;
      case tabs.payments:
        tabContent = (
          <PaymentsTab parentNTNId={NTNId} failedWithData={failedWithData} />
        );
        break;
      case tabs.overview:
      default:
        tabContent = (
          <OverviewTab EETasks={EETasks} claim={claim} NTNId={NTNId} />
        );
        break;
    }

    return tabContent;
  };

  return status !== enums.RefreshStatus.started ? (
    <CardBody expand={ariaExpand}>
      {/* Tab buttons & quick actions dropdown */}
      <div
        className={`${baseShadowMedium} sm:tw-grid sm:tw-grid-cols-12 sm:tw-gap-x-4 tw-gap-y-2 tw-items-center tw-pb-3 tw-px-4 md:tw-py-5 md:tw-px-5 tw-mt-3`}
      >
        <div className="tw-flex tw-gap-1 tw-col-span-12 sm:tw-col-span-7 tw-mb-4 sm:tw-mb-0">
          {Object.values(visibleTabs).map((tabName) => (
            <button
              type="button"
              className={`tw-border-0 tw-py-2 tw-px-2 tw-bg-white
                         tw-w-20 md:tw-w-28 tw-text-sm md:tw-text-lg
                ${
                  tab === tabName
                    ? ' tw-border-b-4 tw-border-solid tw-border-unm-primary02-400 tw-text-unm-interactive01-400'
                    : ' tw-text-unm-adhoc2 tw-pb-3'
                }
              `}
              key={tabName}
              onClick={() => setTab(tabName)}
            >
              {tabName}
            </button>
          ))}
        </div>
        <div className="tw-col-span-12 sm:tw-col-span-5 tw-justify-self-end tw-w-full sm:tw-w-[280px] md:tw-px-0">
          <QuickActionsDropDown dataToPass={NTNId} />
        </div>
      </div>
      <CardItemsContainer className="tw-p-0 md:tw-p-5 sm:tw-pb-8 tw-bg-unm-container-bg">
        {/* cards container */}
        <div className="tw-rounded tw-px-0  ">{renderTabContent()}</div>
      </CardItemsContainer>
    </CardBody>
  ) : (
    <LoadingCover>
      <FadeLoader
        height={13}
        width={4}
        radius={5}
        margin={0}
        color={colors.blue}
      />
    </LoadingCover>
  );
};

const ClaimsCard: (p: IClaimCard) => React.ReactElement = (p) => {
  const {
    claim,
    keyId,
    EETasks,
    ERTasks,
    absenceNotifications,
    personData,
    allTasks,
    showPayments,
    redirectLocation,
    ntnToOpen,
  } = p;

  const history = useHistory();
  const domIds = domIdsUnique(`sc-${keyId}-`);
  const [ariaExpand, setAriaExpand] = useState(false);
  const [tab, setTab] = useState('Overview');
  const NTNId = get(claim, 'NTNId', '');
  const { localizedStrings } = useContext(KenticoDataContext);
  const letters = get(personData, 'documents.letters', []);
  const currentDate = dayjs().format('YYYY-MM-DD');
  const { employerPreferences } = useContext(PreferencesContext);
  const parentCase = absenceNotifications?.absenceNotifications?.find(
    (n) => n.notificationCaseId === NTNId,
  );

  const caseTasks = allTasks?.tasks?.filter(
    (task: dm.Task) => task.notificationCaseId === NTNId,
  );

  const isIntermittent =
    getActiveLeaveType(claim, currentDate) === 'Intermittent' ||
    getActiveLeaveType(claim, currentDate) ===
      'Intermittent Leave as Accommodation Only';

  const subStatusText = (trackerStage: string) => {
    switch (trackerStage) {
      case 'Closed': {
        return getLocalizedOrDefault(
          localizedStrings,
          CaseSubstatusCodenames.closed_4_1,
          CaseSubstatusCodenames.closed_4_1,
        );
      }
      case 'Active': {
        const substatusKontentItemCodename = getManagementScenario(
          claim,
          letters,
          caseTasks,
          currentDate,
        );
        return getLocalizedOrDefault(
          localizedStrings,
          substatusKontentItemCodename,
          substatusKontentItemCodename,
        );
      }
      case 'Review': {
        const substatusKontentItemCodename = getReviewingScenario(
          claim,
          caseTasks,
          currentDate,
          serviceLevelIsEnhanced(employerPreferences),
        );
        return getLocalizedOrDefault(
          localizedStrings,
          substatusKontentItemCodename,
          substatusKontentItemCodename,
        );
      }
      case 'Received': {
        const substatusKontentItemCodename = getReceivedScenario(
          claim,
          currentDate,
        );
        return getLocalizedOrDefault(
          localizedStrings,
          substatusKontentItemCodename,
          substatusKontentItemCodename,
        );
      }
    }
  };

  const dueTasks: dm.Task[] = [];
  EETasks?.forEach((task) => {
    if (
      isDueTask(task.taskStatusId) &&
      taskAppliesToNTN(task.notificationCaseId, NTNId) &&
      !dueTasks.includes(task)
    ) {
      dueTasks.push(task);
    }
  });

  const sortedAssignedTasks = sortAssignedTasks(dueTasks);
  const areTasksDue = sortedAssignedTasks.length > 0;
  const dueTasksContainRequired = checkForRequiredTasks(dueTasks);

  const expandCard = () => {
    setAriaExpand((e) => !e);
  };

  const openOverviewTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAriaExpand(true);
    setTab('Overview');
  };

  const openTasksPage = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`${enums.AppPaths.tasks}/${NTNId}`);
  };

  const openDocumentsPage = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`${enums.AppPaths.documents}/${NTNId}`);
  };

  const openDetailsTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAriaExpand(true);
    setTab('Details');
  };

  const renderBulletWithLinks = (bullet: string) => {
    return bullet.split(/(Tasks|Letters|Details)/g).map((part, index) => {
      if (part === 'Tasks') {
        return (
          <button
            key={`${part}-${index}`}
            type="button"
            onClick={openTasksPage}
            className="tw-inline tw-bg-transparent tw-border-0 tw-p-0 tw-underline tw-text-unm-claimCard-info tw-cursor-pointer"
          >
            {part}
          </button>
        );
      }

      if (part === 'Letters') {
        return (
          <button
            key={`${part}-${index}`}
            type="button"
            onClick={openDocumentsPage}
            className="tw-inline tw-bg-transparent tw-border-0 tw-p-0 tw-underline tw-text-unm-claimCard-info tw-cursor-pointer"
          >
            {part}
          </button>
        );
      }

      if (part === 'Details') {
        return (
          <button
            key={`${part}-${index}`}
            type="button"
            onClick={openDetailsTab}
            className="tw-inline tw-bg-transparent tw-border-0 tw-p-0 tw-underline tw-text-unm-claimCard-info tw-cursor-pointer"
          >
            {part}
          </button>
        );
      }

      return part;
    });
  };

  const trackerStatus = trackerStage(parentCase, claim, currentDate, letters);

  const revealRefs = useRef<Record<string | number, HTMLDivElement | null>>({});

  useEffect(() => {
    if (ntnToOpen === NTNId && revealRefs.current[NTNId]) {
      setTimeout(() => {
        revealRefs.current[NTNId]?.scrollIntoView({ behavior: 'smooth' });
        setTab('Details');
        setAriaExpand(true);
      }, 1500);
    }
  }, [ntnToOpen, NTNId, redirectLocation]);

  return (
    <Card
      id={domIds.rootNode}
      key={NTNId}
      ref={(el) => (revealRefs.current[NTNId] = el)}
    >
      {/*----------------------------------- Mobile View --------------------------*/}
      <CardHeader className="tw-flex-col md:tw-hidden tw-px-4 tw-py-2">
        <div className="tw-flex tw-w-full tw-justify-between tw-items-center">
          <div className="tw-text-unm-interactive01-800 tw-font-Sans tw-font-normal tw-text-2xl">
            {getCardTitle(claim)}
          </div>
          <Column size={9} align={'flex-end tw-mr-0'}>
            <button
              type="button"
              onClick={expandCard}
              className="tw-flex tw-items-center tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
            >
              <Arrow rotate={`${ariaExpand}`} />
            </button>
          </Column>
        </div>
        <div className="tw-flex tw-flex-row tw-w-full tw-justify-between tw-items-center tw-pb-1">
          <Text
            fontSize="base"
            fontWeight="semibold"
            color="unm-primary02-500"
            className="tw-pr-6 tw-break-words tw-leading-[28px]"
          >
            {NTNId}
          </Text>
        </div>
        {claim?.leaves.length > 0 &&
          claim?.leaves[0]?.startDate &&
          claim?.leaves[0]?.endDate && (
            <div className="tw-flex tw-items-center tw-pb-3">
              <Text color="unm-primary02-500" fontSize="base">
                Requested:&nbsp;{' '}
              </Text>
              <Text color="unm-primary02-500" fontSize="base">
                {dayjs(claim?.leaves[0]?.startDate).format(dateFormat)} -{' '}
                {dayjs(claim?.leaves[0]?.endDate).format(dateFormat)}
              </Text>
            </div>
          )}
        <div className="tw-justify-self-end tw-mb-3">
          {dueTasksContainRequired && trackerStatus !== 'Closed' && (
            <StatusFlagModified
              data-testid={`claim-${claim.NTNId}-task-awaiting-action-flag`}
              background={'#FAC832'}
              color={'#2E2E2E'}
              className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-whitespace-nowrap"
            >
              <img
                src={infoCircleOutlineBlack}
                alt=""
                aria-hidden="true"
                className="tw-w-4 tw-h-4 tw-mr-1"
              />
              <span>{enums.TaskUIStatuses.awaiting_action}</span>
            </StatusFlagModified>
          )}
        </div>
        <div className="tw-flex tw-w-full tw-mb-3">
          <div
            className={`tw-flex tw-w-full tw-rounded-lg ${baseShadowMedium}`}
          >
            <StatusTrackerHorizontal
              stage={trackerStage(parentCase, claim, currentDate, letters)}
              ntn={NTNId}
            />
          </div>
        </div>
        {/* Contextual message bar to the right of the status tracker */}
        {(() => {
          const message = getContextualMessage(
            trackerStage(parentCase, claim, currentDate, letters),
            areTasksDue,
            isIntermittent,
          );
          if (!message) {
            return null;
          }
          return (
            <div className="tw-flex tw-flex-col tw-flex-1 tw-bg-unm-adhoc1 tw-rounded-lg tw-p-4">
              <div className="tw-flex tw-w-full tw-mb-2">
                <img
                  alt="info"
                  src={infoIcon}
                  className="tw-self-start tw-pt-1 tw-pr-3"
                />
                <p className="tw-text-sm tw-font-semibold tw-mb-0">
                  {message.label}
                </p>
              </div>
              <div className=" tw-text-sm tw-leading-6 tw-text-unm-claimCard-info">
                <p className="tw-mb-0 tw-font-semibold">{message.heading}</p>
                <ul className="tw-mb-0 tw-ml-2 tw-pl-5 tw-list-disc  tw-font-normal">
                  {message.bullets.map((bullet) => (
                    <li key={bullet}>{renderBulletWithLinks(bullet)}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })()}
      </CardHeader>

      {/*----------------------------------- Desktop View -----------------------------------*/}
      <CardHeader
        className={`tw-hidden md:tw-block tw-flex-col tw-px-6 tw-pt-3 tw-pb-5 ${baseShadowSmall}`}
      >
        <div className="tw-flex tw-w-full md:tw-pb-2 tw-items-center">
          <div
            id={domIds.cardTitle}
            className="tw-flex-row tw-flex-1 tw-min-w-0 tw-text-unm-interactive01-800 tw-font-Sans tw-font-normal tw-text-[28px] tw-leading-[1.2em] tw-break-words"
          >
            {getCardTitle(claim)}
          </div>
          <div className={'tw-pl-2 tw-shrink-0 tw-mr-5'}>
            {dueTasksContainRequired && trackerStatus !== 'Closed' && (
              <StatusFlagModified
                data-testid={`claim-${claim.NTNId}-task-awaiting-action-flag`}
                background={'#FAC832'}
                color={'#2E2E2E'}
                className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-whitespace-nowrap"
              >
                <img
                  src={infoCircleOutlineBlack}
                  alt=""
                  aria-hidden="true"
                  className="tw-w-4 tw-h-4 tw-mr-1"
                />
                <span>{enums.TaskUIStatuses.awaiting_action}</span>
              </StatusFlagModified>
            )}
          </div>
          <Column
            size={12}
            align={'flex-end'}
            className="tw-items-end tw-mr-0 tw-shrink-0"
          >
            <button
              type="button"
              onClick={expandCard}
              className="tw-flex tw-items-center tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
            >
              <span className="tw-text-md tw-font-normal tw--mr-1 tw-text-unm-interactive01-400">
                {ariaExpand ? 'Minimize' : 'View'}
              </span>
              <Arrow id={domIds.dropDownArrow} rotate={`${ariaExpand}`} />
            </button>
          </Column>
        </div>
        <div className="tw-flex tw-flex-row tw-w-full tw-items-center md:tw-pb-5">
          <Text
            fontSize="base"
            fontWeight="semibold"
            color="unm-primary02-500"
            className="tw-pr-6 tw-break-words tw-leading-[28px]"
          >
            {NTNId}
          </Text>
          {claim?.leaves.length > 0 &&
            claim?.leaves[0]?.startDate &&
            claim?.leaves[0]?.endDate && (
              <div className="tw-flex tw-items-center">
                <Text color="unm-primary02-500" fontSize="base">
                  Requested:&nbsp;{' '}
                </Text>
                <Text color="unm-primary02-500" fontSize="base">
                  {dayjs(claim?.leaves[0]?.startDate).format(dateFormat)} -{' '}
                  {dayjs(claim?.leaves[0]?.endDate).format(dateFormat)}
                </Text>
              </div>
            )}
        </div>
        <div className="tw-flex tw-w-full tw-gap-6">
          <div
            className={`tw-flex tw-shrink-0 tw-rounded-lg ${baseShadowMedium}`}
          >
            <StatusTrackerVertical
              stage={trackerStage(parentCase, claim, currentDate, letters)}
              ntn={NTNId}
            />
          </div>
          {/* Contextual message bar to the right of the status tracker */}
          {(() => {
            const message = getContextualMessage(
              trackerStage(parentCase, claim, currentDate, letters),
              areTasksDue,
              isIntermittent,
            );
            if (!message) {
              return null;
            }
            return (
              <div className="tw-flex tw-flex-1 tw-bg-unm-adhoc1 tw-rounded-lg tw-p-4">
                <img
                  alt="info"
                  src={infoIcon}
                  className="tw-self-start tw-pt-1"
                />
                <div className="tw-pl-3 tw-text-base tw-leading-6 tw-text-unm-claimCard-info">
                  <p className="tw-mb-2 tw-font-semibold">{message.label}</p>
                  <p className="tw-mb-0 tw-font-semibold">{message.heading}</p>
                  <ul className="tw-mb-0 tw-list-disc tw-pl-5 tw-font-normal">
                    {message.bullets.map((bullet) => (
                      <li key={bullet}>{renderBulletWithLinks(bullet)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}
        </div>
      </CardHeader>
      <Reveal style={{ display: 'block' }}>
        {ariaExpand ? (
          <ExpandedSection
            {...p}
            NTNId={NTNId}
            ariaExpand={ariaExpand}
            areTasksDue={areTasksDue}
            EETasks={sortedAssignedTasks}
            ERTasks={ERTasks}
            subStatusText={subStatusText}
            currentDate={currentDate}
            letters={letters}
            showPayments={showPayments}
            tab={tab}
            setTab={setTab}
          />
        ) : null}
      </Reveal>
    </Card>
  );
};

export default ClaimsCard;
